import React, { useEffect, useState } from 'react';
import { Rule, RulePayload, RuleScope, RuleTurn, ruleScope } from '../../hooks/usePersonaAdmin';

interface Props {
  rule?: Rule | Partial<Rule> | null;
  /** When true, hides the persona/shared scope toggle and locks scope to `shared`. */
  sharedOnly?: boolean;
  /** Prefilled turns coming from the chat "promote" flow. */
  initialTurns?: RuleTurn[];
  onClose: () => void;
  onSubmit: (payload: RulePayload) => Promise<void> | void;
  onDelete?: () => void;
  isSubmitting?: boolean;
  isDeleting?: boolean;
  title?: string;
}

const EMPTY_TURN: RuleTurn = { text: '', precondition: '' };

/** Keep in sync with peersona orchestrator tiers + fixture `any`. */
const EXPECTED_TIER_OPTIONS = [
  'any',
  'grounded',
  'partial',
  'referral',
  'referral_followup',
  'no_match',
  'crisis',
  'concierge_handoff',
  'clarify',
  'clarify_handoff',
  'clarify_referral',
] as const;

/** Keep in sync with ``VALID_INTENTS`` in app/peersona/intent.py. */
const EXPECTED_INTENT_OPTIONS = [
  'asking_peer',
  'sharing_own',
  'exploring',
  'referral_followup',
  'concierge_request',
  'consent_followup',
] as const;

/** Keep in sync with ``VALID_CONCIERGE_SUBTYPES`` in app/peersona/intent.py. */
const EXPECTED_CONCIERGE_SUBTYPE_OPTIONS = [
  'connect_listener',
  'schedule_call',
  'find_content',
  'platform_help',
] as const;

/** Currently the only orchestrator handoff target. */
const EXPECTED_HANDOFF_TARGET_OPTIONS = ['fi'] as const;

/** Keep in sync with ``PRECONDITION_DESCRIPTIONS`` in app/peersona/eval/scoring.py. */
const PRECONDITION_OPTIONS = ['prior_referral', 'prior_peer_lookup', 'prior_referral_or_peer_lookup'] as const;

const optionsWithCurrent = (options: readonly string[], current: string) => {
  if (current && !options.includes(current)) return [current, ...options];
  return [...options];
};

const OptionalSelect: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  help?: string;
}> = ({ id, label, value, onChange, options, help }) => (
  <div>
    <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">
      {label}
    </label>
    <select
      id={id}
      className="w-full rounded border border-gray-300 bg-white p-2 text-sm focus:ring-2 focus:ring-blue-500"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">(none)</option>
      {optionsWithCurrent(options, value).map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
    {help && <p className="mt-1 text-xs text-gray-500">{help}</p>}
  </div>
);

const RuleEditorDrawer: React.FC<Props> = ({
  rule,
  sharedOnly,
  initialTurns,
  onClose,
  onSubmit,
  onDelete,
  isSubmitting,
  isDeleting,
  title,
}) => {
  const [slug, setSlug] = useState('');
  const [notes, setNotes] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [turns, setTurns] = useState<RuleTurn[]>([EMPTY_TURN]);
  const [expectedTier, setExpectedTier] = useState('');
  const [expectedIntent, setExpectedIntent] = useState('');
  const [expectedConciergeSubtype, setExpectedConciergeSubtype] = useState('');
  const [expectedHandoffTarget, setExpectedHandoffTarget] = useState('');
  const [scope, setScope] = useState<RuleScope>(sharedOnly ? 'shared' : 'persona');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (rule) {
      setSlug(rule.slug || '');
      setNotes(rule.notes || '');
      setTagsInput((rule.tags || []).join(', '));
      setTurns(rule.turns && rule.turns.length > 0 ? rule.turns : [EMPTY_TURN]);
      setExpectedTier(rule.expected_tier || '');
      setExpectedIntent(rule.expected_intent || '');
      setExpectedConciergeSubtype(rule.expected_concierge_subtype || '');
      setExpectedHandoffTarget(rule.expected_handoff_target || '');
      const derivedScope = sharedOnly ? 'shared' : rule.shared !== undefined ? ruleScope(rule as Rule) : 'persona';
      setScope(derivedScope);
      setEnabled(rule.enabled ?? true);
    } else {
      setSlug('');
      setNotes('');
      setTagsInput('');
      setTurns(initialTurns && initialTurns.length > 0 ? initialTurns : [EMPTY_TURN]);
      setExpectedTier('');
      setExpectedIntent('');
      setExpectedConciergeSubtype('');
      setExpectedHandoffTarget('');
      setScope(sharedOnly ? 'shared' : 'persona');
      setEnabled(true);
    }
  }, [rule, sharedOnly, initialTurns]);

  const updateTurn = (idx: number, patch: Partial<RuleTurn>) => {
    setTurns((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const addTurn = () => setTurns((prev) => [...prev, { text: '', precondition: '' }]);
  const removeTurn = (idx: number) => setTurns((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanedTurns = turns
      .map((t) => ({ text: t.text.trim(), precondition: t.precondition?.trim() || undefined }))
      .filter((t) => t.text.length > 0);
    if (!slug.trim()) return;
    if (cleanedTurns.length === 0) return;
    const payload: RulePayload = {
      slug: slug.trim(),
      notes: notes.trim() || undefined,
      tags: tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      turns: cleanedTurns,
      expected_tier: expectedTier.trim() || undefined,
      expected_intent: expectedIntent.trim() || undefined,
      expected_concierge_subtype: expectedConciergeSubtype.trim() || undefined,
      expected_handoff_target: expectedHandoffTarget.trim() || undefined,
      chatbot_id: scope === 'shared' ? null : undefined,
      enabled,
    };
    await onSubmit(payload);
  };

  const isNew = !rule || !(rule as Rule).id;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50">
      <div className="flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {title || (isNew ? 'New Rule' : `Edit Rule · ${rule?.slug}`)}
          </h2>
          <button className="text-2xl leading-none text-gray-400 hover:text-gray-600" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Slug</label>
                <input
                  className="w-full rounded border border-gray-300 p-2 font-mono text-sm focus:ring-2 focus:ring-blue-500"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  required
                  placeholder="e.g. burnout_grounded"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  className="w-full rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Why this rule exists / what it checks"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Tags</label>
                <input
                  className="w-full rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="comma-separated, e.g. safety, referral"
                />
              </div>
            </div>

            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Turns</label>
                <button
                  type="button"
                  className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                  onClick={addTurn}
                >
                  + Add turn
                </button>
              </div>
              <div className="space-y-3">
                {turns.map((turn, idx) => (
                  <div key={idx} className="rounded border border-gray-200 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Turn {idx + 1}
                      </span>
                      {turns.length > 1 && (
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => removeTurn(idx)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <textarea
                      className="mb-2 w-full rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      value={turn.text}
                      onChange={(e) => updateTurn(idx, { text: e.target.value })}
                      placeholder="User message"
                      required
                    />
                    <select
                      className="w-full rounded border border-gray-300 bg-white p-2 text-sm focus:ring-2 focus:ring-blue-500"
                      value={turn.precondition || ''}
                      onChange={(e) => updateTurn(idx, { precondition: e.target.value })}
                    >
                      <option value="">No precondition</option>
                      {optionsWithCurrent(PRECONDITION_OPTIONS, turn.precondition || '').map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <OptionalSelect
                id="expected-tier"
                label="Expected Tier"
                value={expectedTier}
                onChange={setExpectedTier}
                options={EXPECTED_TIER_OPTIONS}
                help="Leave none to skip tier assertions. Use any to accept any tier."
              />
              <OptionalSelect
                id="expected-intent"
                label="Expected Intent"
                value={expectedIntent}
                onChange={setExpectedIntent}
                options={EXPECTED_INTENT_OPTIONS}
              />
              <OptionalSelect
                id="expected-concierge-subtype"
                label="Expected Concierge Subtype"
                value={expectedConciergeSubtype}
                onChange={setExpectedConciergeSubtype}
                options={EXPECTED_CONCIERGE_SUBTYPE_OPTIONS}
                help="Only checked when intent is concierge_request."
              />
              <OptionalSelect
                id="expected-handoff-target"
                label="Expected Handoff Target"
                value={expectedHandoffTarget}
                onChange={setExpectedHandoffTarget}
                options={EXPECTED_HANDOFF_TARGET_OPTIONS}
                help="Usually fi for concierge handoffs."
              />
            </div>

            <div className="mt-6 flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3">
              <div>
                <div className="text-sm font-medium text-gray-700">Scope</div>
                <div className="text-xs text-gray-500">
                  {sharedOnly
                    ? 'Shared rules always apply to every persona.'
                    : 'Shared rules apply to every persona; persona rules apply only to this one.'}
                </div>
              </div>
              {sharedOnly ? (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">shared</span>
              ) : (
                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    className={`rounded px-3 py-1 font-medium ${
                      scope === 'persona' ? 'bg-blue-primary text-white' : 'bg-gray-200 text-gray-700'
                    }`}
                    onClick={() => setScope('persona')}
                  >
                    Persona
                  </button>
                  <button
                    type="button"
                    className={`rounded px-3 py-1 font-medium ${
                      scope === 'shared' ? 'bg-blue-primary text-white' : 'bg-gray-200 text-gray-700'
                    }`}
                    onClick={() => setScope('shared')}
                  >
                    Shared
                  </button>
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <input
                id="rule-enabled"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <label htmlFor="rule-enabled" className="text-sm text-gray-700">
                Enabled
              </label>
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-between border-t bg-gray-50 px-6 py-3">
            {!isNew && onDelete ? (
              <button
                type="button"
                className="rounded px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                onClick={() => {
                  if (window.confirm('Delete this rule?')) onDelete();
                }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-primary rounded px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving…' : isNew ? 'Create Rule' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RuleEditorDrawer;
